import { useState, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Receipt, Maximize2, Minimize2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { generateBillNumber } from "@/lib/naming";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtRate = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wo: any;
  woLines: any[];
  projectId: string;
  onCreated: () => void;
}

export const WOCreateBillDialog = ({ open, onOpenChange, wo, woLines, projectId, onCreated }: Props) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // partial qty: map of wol.id → billing qty (defaults to full qty when selected)
  const [billingQtys, setBillingQtys] = useState<Record<string, number>>({});
  const [billDate, setBillDate] = useState(new Date().toISOString().split("T")[0]);
  const [dueDate, setDueDate] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Only show lines that can be billed (pending or complete, not already billed/paid/voided/released)
  const billableLines = useMemo(() => {
    return woLines.filter((wol: any) =>
      wol.work_order_id === wo.id &&
      ["pending", "complete"].includes(wol.status)
    );
  }, [woLines, wo.id]);

  // Consolidate by sov_line_id for display
  const consolidatedGroups = useMemo(() => {
    const groups: Record<string, { sovLineId: string; description: string; unit: string; lines: any[] }> = {};
    billableLines.forEach((wol: any) => {
      const key = wol.sov_line_id ?? wol.id;
      if (!groups[key]) {
        groups[key] = {
          sovLineId: key,
          description: wol.description,
          unit: wol.unit ?? "",
          lines: [],
        };
      }
      groups[key].lines.push(wol);
    });
    return Object.values(groups);
  }, [billableLines]);

  const toggleLine = useCallback((wolId: string, fullQty: number) => {
    // Check current state outside setter to avoid calling a setter inside a setter callback
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(wolId)) next.delete(wolId);
      else next.add(wolId);
      return next;
    });
    setBillingQtys(prev => {
      if (prev[wolId] !== undefined) {
        const { [wolId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [wolId]: fullQty };
    });
  }, []);

  const updateBillingQty = useCallback((wolId: string, maxQty: number, val: string) => {
    const parsed = parseFloat(val);
    if (isNaN(parsed) || parsed <= 0) return;
    const clamped = Math.min(parsed, maxQty);
    setBillingQtys(q => ({ ...q, [wolId]: clamped }));
  }, []);

  const toggleGroup = (lines: any[]) => {
    const allSelected = lines.every(l => selectedIds.has(l.id));
    const newIds = new Set(selectedIds);
    const newQtys = { ...billingQtys };
    lines.forEach(l => {
      if (allSelected) {
        newIds.delete(l.id);
        delete newQtys[l.id];
      } else {
        newIds.add(l.id);
        newQtys[l.id] = Number(l.quantity);
      }
    });
    setSelectedIds(newIds);
    setBillingQtys(newQtys);
  };

  const selectAll = () => {
    if (selectedIds.size === billableLines.length) {
      setSelectedIds(new Set());
      setBillingQtys({});
    } else {
      const newSet = new Set(billableLines.map(l => l.id));
      const newQtys: Record<string, number> = {};
      billableLines.forEach(l => { newQtys[l.id] = Number(l.quantity); });
      setSelectedIds(newSet);
      setBillingQtys(newQtys);
    }
  };

  const selectedLines = billableLines.filter(l => selectedIds.has(l.id));

  // Use billing qty (partial) where available, otherwise full qty
  const getBillingQty = (wol: any) => billingQtys[wol.id] ?? Number(wol.quantity);

  const subtotal = selectedLines.reduce((s, wol) =>
    s + getBillingQty(wol) * Number(wol.contract_rate), 0);

  const retainagePct = Number(wo.retainage_pct ?? 10);
  const retainageAmount = subtotal * (retainagePct / 100);
  const total = subtotal - retainageAmount;

  const handleCreateBill = async () => {
    if (selectedIds.size === 0) return toast.error("Select at least one line to bill");
    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      const userId = (await supabase.auth.getUser()).data.user?.id;

      // Generate bill number
      const billNumber = await generateBillNumber(wo.id, wo.number);

      // Create bill
      const { data: bill, error: billErr } = await supabase.from("bills").insert({
        company_id: companyId,
        project_id: projectId,
        work_order_id: wo.id,
        vendor_id: wo.contractor_id,
        bill_number: billNumber,
        bill_date: billDate,
        due_date: dueDate || null,
        description: description || `Bill from ${wo.number}`,
        subtotal,
        retainage_percent: retainagePct,
        retainage_amount: retainageAmount,
        total,
        status: "draft",
        created_by: userId,
      }).select("id, bill_number").single();

      if (billErr) throw billErr;

      // Create bill lines (using partial qty if applicable)
      const billLines = selectedLines.map(wol => {
        const billingQty = getBillingQty(wol);
        return {
          bill_id: bill.id,
          company_id: companyId,
          description: `${wol.description}${wol.unit_schedule?.unit_number ? ` — Unit ${wol.unit_schedule.unit_number}` : ""}`,
          quantity: billingQty,
          unit_price: Number(wol.contract_rate),
          line_total: billingQty * Number(wol.contract_rate),
          sov_line_id: wol.sov_line_id || null,
          work_order_line_id: wol.id,
        };
      });

      const { error: blErr } = await supabase.from("bill_lines").insert(billLines);
      if (blErr) throw blErr;

      // Update WO lines status — partial bill keeps line as 'billed' (rebillable for remainder handled separately)
      for (const wol of selectedLines) {
        const billingQty = getBillingQty(wol);
        const isPartial = billingQty < Number(wol.quantity);
        await supabase.from("work_order_lines").update({
          // Keep as 'billed' whether partial or full; partial remainder tracked via billed_amount
          status: "billed",
          billed_amount: billingQty * Number(wol.contract_rate),
        } as any).eq("id", wol.id);
      }

      // Audit log
      await supabase.from("ops_audit_log").insert({
        table_name: "bills",
        record_id: bill.id,
        action: "bill_created",
        new_status: "draft",
        changed_by: userId,
        metadata: {
          wo_number: wo.number,
          bill_number: bill.bill_number,
          lines_count: selectedIds.size,
          subtotal,
          retainage: retainageAmount,
          total,
        },
      } as any);

      toast.success(`${bill.bill_number} created — ${selectedIds.size} lines | Total: ${fmt(total)}`);
      setSelectedIds(new Set());
      setBillingQtys({});
      onOpenChange(false);
      onCreated();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "transition-all duration-200 flex flex-col p-0",
        expanded ? "max-w-[95vw] h-[95vh]" : "max-w-4xl max-h-[90vh]"
      )}>
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0 flex-row items-center justify-between">
          <div>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" /> Create Bill — {wo.number}
            </DialogTitle>
            <DialogDescription>
              Select lines to bill from this work order. {billableLines.length} billable line{billableLines.length !== 1 ? "s" : ""} available.
            </DialogDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setExpanded(e => !e)} className="shrink-0">
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Bill metadata */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Bill Date</label>
              <Input type="date" value={billDate} onChange={e => setBillDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Due Date (optional)</label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Description (optional)</label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={`Bill from ${wo.number}`}
              rows={2}
            />
          </div>

          {/* Line selection */}
          {billableLines.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No billable lines — all lines are already billed, paid, or voided.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedIds.size === billableLines.length && billableLines.length > 0}
                  onCheckedChange={selectAll}
                  onClick={e => e.stopPropagation()}
                />
                <span className="text-sm font-medium cursor-pointer" onClick={selectAll}>
                  Select All ({billableLines.length})
                </span>
              </div>

              <div className="border rounded-md overflow-y-auto" style={{ maxHeight: expanded ? undefined : 350 }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">WO Qty</TableHead>
                      <TableHead className="text-right w-28">Bill Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Bill Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {consolidatedGroups.map(group => {
                      const groupSelected = group.lines.every(l => selectedIds.has(l.id));
                      const groupPartial = group.lines.some(l => selectedIds.has(l.id)) && !groupSelected;

                      if (group.lines.length === 1) {
                        const wol = group.lines[0];
                        const fullQty = Number(wol.quantity);
                        const rate = Number(wol.contract_rate);
                        const isSelected = selectedIds.has(wol.id);
                        const billQty = getBillingQty(wol);
                        return (
                          <TableRow
                            key={wol.id}
                            className={cn("cursor-pointer", isSelected && "bg-primary/5")}
                            onClick={() => toggleLine(wol.id, fullQty)}
                          >
                            <TableCell>
                              {/* stopPropagation prevents row onClick from double-firing */}
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleLine(wol.id, fullQty)}
                                onClick={e => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell className="text-sm">
                              {wol.description}
                              {wol.unit_schedule?.unit_number && (
                                <span className="text-muted-foreground ml-1">· Unit {wol.unit_schedule.unit_number}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-muted-foreground">{fullQty.toLocaleString()}</TableCell>
                            <TableCell className="text-right">
                              {isSelected ? (
                                <Input
                                  type="number"
                                  min={0.01}
                                  max={fullQty}
                                  step="any"
                                  value={billQty}
                                  className="w-24 h-7 text-right text-sm"
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => updateBillingQty(wol.id, fullQty, e.target.value)}
                                />
                              ) : (
                                <span className="text-sm text-muted-foreground">—</span>
                              )}
                              {isSelected && billQty < fullQty && (
                                <div className="text-[10px] text-amber-600 mt-0.5">partial</div>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{wol.unit ?? ""}</TableCell>
                            <TableCell className="text-right text-sm">{fmtRate(rate)}</TableCell>
                            <TableCell className="text-right font-medium text-sm">
                              {isSelected ? fmt(billQty * rate) : fmt(fullQty * rate)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">{wol.status}</Badge>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      // Multi-line group
                      const totalQty = group.lines.reduce((s: number, l: any) => s + Number(l.quantity), 0);
                      const rate = Number(group.lines[0].contract_rate);
                      const groupTotal = group.lines.reduce((s: number, l: any) => s + Number(l.quantity) * Number(l.contract_rate), 0);

                      return (
                        <TableRow
                          key={group.sovLineId}
                          className={cn("cursor-pointer", groupSelected && "bg-primary/5")}
                          onClick={() => toggleGroup(group.lines)}
                        >
                          <TableCell>
                            <Checkbox
                              checked={groupSelected}
                              // @ts-ignore
                              indeterminate={groupPartial}
                              onCheckedChange={() => toggleGroup(group.lines)}
                              onClick={e => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell className="text-sm">
                            {group.description}
                            <span className="text-muted-foreground ml-1">· {group.lines.length} units</span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm text-muted-foreground">{totalQty.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">—</TableCell>
                          <TableCell className="text-sm">{group.unit}</TableCell>
                          <TableCell className="text-right text-sm">{fmtRate(rate)}</TableCell>
                          <TableCell className="text-right font-medium text-sm">{fmt(groupTotal)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-[10px]">{group.lines[0].status}</Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {/* Summary */}
          {selectedIds.size > 0 && (
            <div className="border rounded-md p-3 space-y-1 bg-muted/30 text-sm">
              <div className="text-xs font-semibold text-muted-foreground mb-1">BILL SUMMARY</div>
              <div className="flex justify-between">
                <span>Lines selected</span>
                <span className="font-medium">{selectedIds.size}</span>
              </div>
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-medium">{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Retainage ({retainagePct}%)</span>
                <span>−{fmt(retainageAmount)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-1">
                <span>Bill Total</span>
                <span>{fmt(total)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreateBill} disabled={busy || selectedIds.size === 0}>
            {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Creating…</> : `Create Bill (${selectedIds.size} lines) →`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
